import {
  Block,
  BlockTitle,
  Popup as F7Popup,
  NavRight,
  NavTitle,
  Navbar,
  Page,
  f7ready,
} from 'framework7-react';
import {
  CSSProperties,
  ErrorInfo,
  FC,
  PropsWithChildren,
  RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Popup as PopupType } from 'framework7/types';

import { useAnalytics } from '@hooks/useAnalytics';

import IconButton from '@components/buttons/IconButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { LineAwesomeIcon } from '@components/icons/LineAwesomeIcon';

import { ErrorBoundary } from '../ErrorBoundary';
import './Popup.scss';

const PageContent = () => (
  <Page>
    <div slot="fixed" className="swipe-handler"></div>
    <BlockTitle large>Hello!</BlockTitle>
    <Block strongIos outlineIos>
      <p>
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse faucibus mauris leo, eu
        bibendum neque congue non. Ut leo mauris, eleifend eu commodo a, egestas ac urna. Maecenas
        in lacus faucibus, viverra ipsum pulvinar, molestie arcu. Etiam lacinia venenatis dignissim.
        Suspendisse non nisl semper tellus malesuada suscipit eu et eros. Nulla eu enim quis quam
        elementum vulputate. Mauris ornare consequat nunc viverra pellentesque. Aenean semper eu
        massa sit amet aliquam. Integer et neque sed libero mollis elementum at vitae ligula.
        Vestibulum pharetra sed libero sed porttitor. Suspendisse a faucibus lectus.
      </p>
      <p>
        Duis ut mauris sollicitudin, venenatis nisi sed, luctus ligula. Phasellus blandit nisl ut
        lorem semper pharetra. Nullam tortor nibh, suscipit in consequat vel, feugiat sed quam. Nam
        risus libero, auctor vel tristique ac, malesuada ut ante. Sed molestie, est in eleifend
        sagittis, leo tortor ullamcorper erat, at vulputate eros sapien nec libero. Mauris dapibus
        laoreet nibh quis bibendum. Fusce dolor sem, suscipit in iaculis id, pharetra at urna.
        Pellentesque tempor congue massa quis faucibus. Vestibulum nunc eros, convallis blandit dui
        sit amet, gravida adipiscing libero.
      </p>
      <p>
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse faucibus mauris leo, eu
        bibendum neque congue non. Ut leo mauris, eleifend eu commodo a, egestas ac urna. Maecenas
        in lacus faucibus, viverra ipsum pulvinar, molestie arcu. Etiam lacinia venenatis dignissim.
        Suspendisse non nisl semper tellus malesuada suscipit eu et eros. Nulla eu enim quis quam
        elementum vulputate. Mauris ornare consequat nunc viverra pellentesque. Aenean semper eu
        massa sit amet aliquam. Integer et neque sed libero mollis elementum at vitae ligula.
        Vestibulum pharetra sed libero sed porttitor. Suspendisse a faucibus lectus.
      </p>
      <p>
        Duis ut mauris sollicitudin, venenatis nisi sed, luctus ligula. Phasellus blandit nisl ut
        lorem semper pharetra. Nullam tortor nibh, suscipit in consequat vel, feugiat sed quam. Nam
        risus libero, auctor vel tristique ac, malesuada ut ante. Sed molestie, est in eleifend
        sagittis, leo tortor ullamcorper erat, at vulputate eros sapien nec libero. Mauris dapibus
        laoreet nibh quis bibendum. Fusce dolor sem, suscipit in iaculis id, pharetra at urna.
        Pellentesque tempor congue massa quis faucibus. Vestibulum nunc eros, convallis blandit dui
        sit amet, gravida adipiscing libero.
      </p>
    </Block>
  </Page>
);

interface PopupContextValue {
  popupRef: RefObject<PopupType.Popup>;
}

const popupContext = createContext<PopupContextValue>({
  popupRef: { current: null },
});

export const usePopupContext = () => {
  const context = useContext(popupContext);
  if (!context) {
    throw new Error('usePopupContext must be used within a PopupProvider');
  }
  return context;
};

export interface PopupProps {
  isVisible: boolean;
  className?: string;
  height?: 'half' | '3quarters' | 'full';
  title?: string;
  onClose: () => void;
  disableClose?: boolean;
  style?: CSSProperties;
}

export const PopupUnwrapped = ({
  isVisible: controlledIsVisible,
  onClose,
  title,
  height = 'half',
  disableClose,
  children,
  className = '',
  style,
}: PropsWithChildren<PopupProps>) => {
  const [isVisible, setIsVisible] = useState(false);
  const divRef = useRef<any>(null);
  const popupRef = useRef<PopupType.Popup>(null);
  const { track } = useAnalytics();

  useEffect(() => {
    setIsVisible(controlledIsVisible);
  }, [controlledIsVisible]);

  useEffect(() => {
    f7ready((f7) => {
      popupRef.current = f7.popup.create({
        el: divRef.current,
        swipeToClose: true,
        swipeHandler: '.swipe-handler',
        on: {
          swipeClose: () => {
            track('Popup Swipe Closed');
          },
          closed: () => {
            onClose();
          },
        },
      });
    });

    return () => {
      if (popupRef.current) {
        // popupRef.current.close();
        // popupRef.current.destroy();
        // popupRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (popupRef.current) {
      if (isVisible) {
        popupRef.current.open();
      } else {
        popupRef.current.close();
      }
    }
  }, [isVisible]);

  return (
    <div
      ref={divRef}
      className={`popup popup-height-${height} ${className}`}
      data-close-on-escape={disableClose ? undefined : 'true'}
      data-swipe-to-close={disableClose ? undefined : 'to-bottom'}
      style={style}
    >
      <Page>
        {height === 'full' ? (
          <Navbar>
            {title && <NavTitle>{title}</NavTitle>}
            {!disableClose && (
              <NavRight>
                <IconButton
                  color="neutral"
                  variant="flat"
                  onClick={() => {
                    popupRef.current.close();
                  }}
                >
                  <LineAwesomeIcon name="times" style={{ fontSize: 24 }} />
                </IconButton>
              </NavRight>
            )}
          </Navbar>
        ) : (
          <div slot="fixed" className="swipe-handler">
            <div />
          </div>
        )}
        <div>
          <popupContext.Provider value={{ popupRef }}>{children}</popupContext.Provider>
        </div>
      </Page>
    </div>
  );
};

export const Popup = (props: PropsWithChildren<PopupProps>) => {
  const handlePopupError = useCallback((error: Error, errorInfo: ErrorInfo) => {
    console.error('Popup Error', { error, errorInfo });
  }, []);

  return (
    <ErrorBoundary hideOnError onError={handlePopupError}>
      <PopupUnwrapped {...props} />
    </ErrorBoundary>
  );
};

const PopupScrollabelContent: FC<PropsWithChildren<{}>> = ({ children }) => {
  return (
    <Page>
      <div slot="fixed" className="swipe-handler">
        <div />
      </div>
      <div className="popup-content">{children}</div>
    </Page>
  );
};

Popup.ScrollableContent = PopupScrollabelContent;

const PopupContent: FC<PropsWithChildren<{}>> = ({ children }) => {
  return (
    <div className="popup-content">
      <ErrorBoundary>{children}</ErrorBoundary>
    </div>
  );
};

Popup.Content = PopupContent;
